import React, { useMemo, useState } from 'react';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { categoryLabel } from '../features/product_knowledge/productCategories';
import { Dash, RatioScore, Tag, chip, scoreColor, th, td, firstCell } from './listPage';

/**
 * 직원 홈 › 제품 지식: one row per product with both sources side by side —
 * 롤플레이 설명 정확도 (claims checked in roleplay) and 퀴즈 정답률 (first answer per question).
 * It replaces two Top-10 charts that each picked their own products, showed % without counts,
 * and (the quiz one) ranked by 정답률, so weak products fell off the bottom.
 */

const TOP = 10;
// Below this many items a rate is shown but marked: 1/1 = 100% is not a strong product.
const MIN_SAMPLE = 5;

const SORTS = [
  ['volume', '많이 다룬 순'],
  ['pk', '설명 정확도 낮은 순'],
  ['quiz', '퀴즈 정답률 낮은 순'],
];

const total = r => (r.pk?.checked || 0) + (r.quiz?.checked || 0);
// "Low first" puts thin samples and missing data last, or a single 0/1 would lead the list.
const lowFirst = key => (a, b) => {
  const rank = r => (!r[key] ? 2 : r[key].checked < MIN_SAMPLE ? 1 : 0);
  return rank(a) - rank(b) || (a[key]?.rate ?? 0) - (b[key]?.rate ?? 0) || total(b) - total(a);
};

const Measure = ({ m }) => {
  if (!m) return <Dash />;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
      <div style={{ flex: 1, minWidth: '60px', maxWidth: '160px', height: '8px', borderRadius: '4px', backgroundColor: '#F2F2F7', overflow: 'hidden' }}>
        <div style={{ width: `${m.rate}%`, height: '100%', backgroundColor: scoreColor(m.rate), opacity: m.checked < MIN_SAMPLE ? 0.45 : 1 }} />
      </div>
      <RatioScore num={m.correct} den={m.checked} />
      {m.checked < MIN_SAMPLE && <Tag>표본 적음</Tag>}
    </div>
  );
};

const ProductKnowledgeByProduct = ({ rows = [] }) => {
  const { isMobile } = useBreakpoint();
  const [sort, setSort] = useState('volume');
  const [all, setAll] = useState(false);

  const sorted = useMemo(
    () => (sort === 'volume' ? rows : [...rows].sort(lowFirst(sort))),
    [rows, sort],
  );
  const shown = all ? sorted : sorted.slice(0, TOP);
  const name = r => (r.category === 'service' ? '가격·서비스 안내' : r.product_name);

  return (
    <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: isMobile ? '16px' : '24px', boxShadow: '0 2px 10px rgba(0,0,0,0.03)' }}>
      <div style={{ fontSize: '16px', fontWeight: 700, color: '#1d1d1f' }}>제품별 설명 정확도 · 퀴즈 정답률</div>
      <div style={{ fontSize: '13px', color: '#86868b', margin: '4px 0 14px', lineHeight: 1.5 }}>
        <b style={{ color: '#424245' }}>설명 정확도</b>는 롤플레이에서 직원이 고객에게 설명한 내용 중 맞은 비율,{' '}
        <b style={{ color: '#424245' }}>퀴즈 정답률</b>은 퀴즈를 처음 풀었을 때 맞힌 비율입니다.
      </div>
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}>
        {SORTS.map(([key, label]) => (
          <button key={key} type="button" style={chip(sort === key)} onClick={() => setSort(key)}>{label}</button>
        ))}
      </div>

      {rows.length === 0 ? (
        <div style={{ padding: '32px 0', textAlign: 'center', color: '#86868b', fontSize: '14px' }}>아직 제품 데이터가 없습니다.</div>
      ) : isMobile ? (
        <div>
          {shown.map(r => (
            <div key={r.product_id} style={{ padding: '12px 0', borderTop: '1px solid rgba(0,0,0,0.05)', display: 'grid', gap: '6px' }}>
              <div style={{ fontSize: '15px', fontWeight: 600, color: '#1d1d1f' }}>
                {name(r)} <span style={{ fontSize: '12px', fontWeight: 400, color: '#86868b' }}>{categoryLabel(r.category)}</span>
              </div>
              {[['설명 정확도', r.pk], ['퀴즈 정답률', r.quiz]].map(([label, m]) => (
                <div key={label} style={{ display: 'grid', gridTemplateColumns: '80px minmax(0, 1fr)', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '12px', color: '#86868b' }}>{label}</span>
                  <Measure m={m} />
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...firstCell(th), paddingLeft: 0 }}>제품</th>
              <th style={th}>롤플레이 설명 정확도</th>
              <th style={th}>퀴즈 정답률 (첫 시도)</th>
            </tr>
          </thead>
          <tbody>
            {shown.map(r => (
              <tr key={r.product_id}>
                <td style={{ ...firstCell(td), paddingLeft: 0 }}>
                  <div style={{ fontWeight: 600 }}>{name(r)}</div>
                  <div style={{ fontSize: '12px', color: '#86868b' }}>{categoryLabel(r.category)}</div>
                </td>
                <td style={{ ...td, width: '36%' }}><Measure m={r.pk} /></td>
                <td style={{ ...td, width: '36%' }}><Measure m={r.quiz} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {sorted.length > TOP && (
        <div style={{ textAlign: 'center', marginTop: '12px' }}>
          <button type="button" style={chip(false)} onClick={() => setAll(v => !v)}>
            {all ? '접기' : `모두 보기 (${sorted.length}개 제품)`}
          </button>
        </div>
      )}
    </div>
  );
};

export default ProductKnowledgeByProduct;
